import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiErrorCode, getApiErrorDetails } from '@/shared/lib/api-errors';
import { serverExtraBrandQuote, type BillingInterval, type ExtraBrandQuote } from '@/shared/lib/account-pricing';
import { openRazorpaySubscription } from '@/shared/lib/razorpay';
import { billingService } from '@/shared/services/billing.service';

/**
 * What an extra-brand refusal means for the screen that sent the request
 * (ADR-006: adding a brand, or moving one into a full Agency account):
 * - quote: show the server's price and new total, then resend with consent;
 * - block: no price can be offered (`noQuote`) or the Agency is not a paid
 *   subscription (`paidAgency`), so say why and offer nothing;
 * - authorised: Razorpay's extra-brand subscription was just authorised and
 *   confirmed, so resend with consent;
 * - error: a translated message to show.
 */
export type ExtraBrandOutcome =
  | { kind: 'quote'; quote: ExtraBrandQuote }
  | { kind: 'block'; block: 'noQuote' | 'paidAgency' }
  | { kind: 'authorised' }
  | { kind: 'error'; message: string };

interface ExtraBrandConsentOptions {
  /**
   * What is being bought, for the messages: a brand being created (nothing
   * was created on failure) or room added ahead of time (no room was added).
   */
  context?: 'brand' | 'room';
  /** The interval to assume when the server's quote does not say. */
  interval: BillingInterval;
  /** Extra brands to assume when the server does not count them. */
  fallbackExtras: (details: Record<string, unknown>) => number;
  /** The brand whose account is billed, for the Razorpay confirm (X-Workspace-Id). */
  confirmWorkspaceId?: string | null;
}

export interface ExtraBrandConsent {
  /** Razorpay is open for the extra-brand subscription. */
  authorising: boolean;
  /** Reads an API error; null when it is not an extra-brand refusal. */
  handle: (err: unknown) => Promise<ExtraBrandOutcome | null>;
}

/** The shared handling of extra-brand refusals, for every flow that can add a brand to an Agency. */
export function useExtraBrandConsent({ context = 'brand', interval, fallbackExtras, confirmWorkspaceId }: ExtraBrandConsentOptions): ExtraBrandConsent {
  const { t } = useTranslation();
  const [authorising, setAuthorising] = useState(false);
  const cancelledKey = context === 'room' ? 'settings.billingTab.addRoomAuthCancelled' : 'topbar.addBrandSheet.authorisationCancelled';
  const failedKey = context === 'room' ? 'settings.billingTab.addRoomAuthFailed' : 'topbar.addBrandSheet.authorisationFailed';

  const authorise = async (details: Record<string, unknown>): Promise<ExtraBrandOutcome> => {
    const subscriptionId = typeof details['subscriptionId'] === 'string' ? details['subscriptionId'] : null;
    const razorpayKeyId = typeof details['razorpayKeyId'] === 'string' ? details['razorpayKeyId'] : null;
    if (!subscriptionId || !razorpayKeyId) return { kind: 'error', message: t(failedKey) };
    setAuthorising(true);
    try {
      // No amount: Razorpay shows the subscription's own (in paise, not rupees).
      const response = await openRazorpaySubscription({
        razorpayKeyId,
        subscriptionId,
        description: t('topbar.addBrandSheet.authorisationDescription'),
      });
      if (!response) return { kind: 'error', message: t(cancelledKey) };
      await billingService.confirmPayment(
        response.razorpay_payment_id,
        response.razorpay_signature,
        response.razorpay_subscription_id,
        confirmWorkspaceId ? { workspaceId: confirmWorkspaceId } : {},
      );
      return { kind: 'authorised' };
    } catch {
      return { kind: 'error', message: t(failedKey) };
    } finally {
      setAuthorising(false);
    }
  };

  const handle = async (err: unknown): Promise<ExtraBrandOutcome | null> => {
    const code = getApiErrorCode(err);
    const details = getApiErrorDetails(err);
    switch (code) {
      case 'EXTRA_BRAND_AUTHORIZATION_REQUIRED':
        // Rupees: the first extra brand opens a second Razorpay subscription,
        // authorised here; once confirmed, the same request fits the limit.
        return authorise(details);
      case 'EXTRA_BRAND_REQUIRED': {
        // The price is the server's, in the currency the account is charged
        // in. Without one, nothing is priced and nothing is offered.
        const quote = serverExtraBrandQuote(details, { interval, extrasAfter: fallbackExtras(details) });
        return quote ? { kind: 'quote', quote } : { kind: 'block', block: 'noQuote' };
      }
      case 'EXTRA_BRAND_ADMIN_MANAGED':
      case 'EXTRA_BRAND_REQUIRES_PAID_AGENCY':
      case 'EXTRA_BRAND_NOT_ON_PLAN':
        // Set up by our team (admin comp, timed or not) or a cancelled Agency.
        return { kind: 'block', block: 'paidAgency' };
      case 'EXTRA_BRAND_BILLING_UNAVAILABLE':
        return { kind: 'error', message: t('account.extraBrandBillingUnavailable') };
      default:
        return null;
    }
  };

  return { authorising, handle };
}
